import React from "react";
import { motion } from "framer-motion";
import {
  Factory, HeartPulse, Pill, Landmark, GraduationCap, Cpu, HardHat, ShoppingBag, Building2, ArrowUpRight, ShieldCheck,
} from "lucide-react";
import { INDUSTRIES, INDUSTRY_DETAILS } from "../data/content";
import { useModals } from "./modals/ModalProvider";

const iconMap = {
  factory: Factory,
  "heart-pulse": HeartPulse,
  pill: Pill,
  landmark: Landmark,
  "graduation-cap": GraduationCap,
  cpu: Cpu,
  "hard-hat": HardHat,
  "shopping-bag": ShoppingBag,
  "building-2": Building2,
};

export default function IndustriesGrid() {
  const { openIndustryDetail } = useModals();

  return (
    <section id="industries" className="py-20 sm:py-28 relative">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="max-w-2xl">
          <p className="text-sm font-semibold uppercase tracking-[0.22em] text-secondary">Industries</p>
          <h2 className="mt-3 font-display text-3xl sm:text-4xl lg:text-5xl font-extrabold tracking-tight">
            Industry playbooks <span className="text-gradient-brand">at your fingertips.</span>
          </h2>
          <p className="mt-4 text-muted-foreground text-base sm:text-lg leading-relaxed">
            Click any industry to explore the exact IT, data privacy and security compliance frameworks
            that apply, along with the security controls we deploy to keep you audit-ready.
          </p>
        </div>

        <div className="mt-12 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
          {INDUSTRIES.map((it, i) => {
            const Icon = iconMap[it.icon] || Factory;
            const detail = INDUSTRY_DETAILS[it.id];
            const complianceCount =
              (detail?.itCompliance?.length || 0) +
              (detail?.dataCompliance?.length || 0) +
              (detail?.securityCompliance?.length || 0);
            const controlCount = detail?.controls?.length || 0;

            return (
              <motion.button
                key={it.id}
                initial={{ opacity: 0, y: 24 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-40px" }}
                transition={{ duration: 0.5, delay: i * 0.05 }}
                onClick={() => openIndustryDetail(it)}
                data-testid={`industry-card-${it.id}`}
                className="group relative text-left overflow-hidden rounded-3xl border border-border bg-card card-lift focus:outline-none focus:ring-2 focus:ring-primary/40"
              >
                {/* Hero image */}
                <div className="relative h-40 sm:h-44 overflow-hidden">
                  <img
                    src={it.image}
                    alt={it.title}
                    loading="lazy"
                    className="absolute inset-0 w-full h-full object-cover group-hover:scale-110 transition-transform duration-700 ease-out"
                  />
                  <div
                    className="absolute inset-0"
                    style={{
                      background: `linear-gradient(180deg, ${it.accent}00 0%, ${it.accent}80 60%, ${it.accent}F0 100%)`,
                    }}
                  />
                  <div className="absolute inset-0 p-4 flex items-start justify-between">
                    <div className="h-11 w-11 rounded-2xl bg-white/15 backdrop-blur-md border border-white/25 grid place-items-center text-white shadow-lg">
                      <Icon className="h-5 w-5" strokeWidth={1.7} />
                    </div>
                    <div className="h-8 w-8 rounded-full bg-white/15 backdrop-blur-md border border-white/25 grid place-items-center text-white opacity-0 group-hover:opacity-100 transition-opacity">
                      <ArrowUpRight className="h-4 w-4" />
                    </div>
                  </div>
                  <div className="absolute bottom-0 inset-x-0 px-4 pb-3">
                    <h3 className="font-display text-white text-xl sm:text-2xl font-extrabold tracking-tight drop-shadow">
                      {it.title}
                    </h3>
                  </div>
                </div>

                {/* Body */}
                <div className="p-5">
                  <p className="text-sm text-muted-foreground leading-relaxed line-clamp-2">{it.desc}</p>
                  <div className="mt-4 grid grid-cols-2 gap-2">
                    <div className="rounded-xl border border-border p-2.5">
                      <div className="text-[9px] uppercase tracking-widest text-muted-foreground">Compliances</div>
                      <div className="mt-0.5 font-display text-lg font-bold text-primary">{complianceCount}+</div>
                    </div>
                    <div className="rounded-xl border border-border p-2.5">
                      <div className="text-[9px] uppercase tracking-widest text-muted-foreground">Controls</div>
                      <div className="mt-0.5 font-display text-lg font-bold" style={{ color: it.accent }}>{controlCount}+</div>
                    </div>
                  </div>
                  <div
                    data-testid={`industry-explore-${it.id}`}
                    className="mt-4 inline-flex items-center gap-1.5 text-sm font-semibold text-primary group-hover:text-primary/80"
                  >
                    <ShieldCheck className="h-4 w-4" /> Explore compliance & controls
                    <ArrowUpRight className="h-3.5 w-3.5" />
                  </div>
                </div>
              </motion.button>
            );
          })}
        </div>
      </div>
    </section>
  );
}
